// src/pages/FamilyDashboardPage/FamilyDashboardPage.jsx
import React, { useContext, useEffect, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css"; // Import toast styles
import useAxios from "../../../hooks/useAxios";
import "./FamilyDashboardPage.css";
import { 
    Box, 
    CircularProgress, 
    Container, 
    Typography, 
    Button, 
    useMediaQuery, 
    Grid, 
    Tooltip,
    IconButton
} from "@mui/material";
import Header from "../../../components/Header/Header";
import QuizCard from "../../../components/QuizComponents/Cards/QuizCard/QuizCard";
import ActivateAccountAlert from "../../../components/Auth/ActivateAccountAlert/ActivateAccountAlert";
import ContentHeader from "../../../components/common/ContentHeader/ContentHeader";
import StudentAddQuizModal from "../../../components/DashboardComponents/Modals/StudentAddQuizModal/StudentAddQuizModal";
import { useTheme } from "@mui/material/styles";
import StudentReportTable from "../../../components/DashboardComponents/StudentDashboard/StudentReportTable/StudentReportTable";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../../context/AuthContext";
import ConfirmationModal from "../../../components/common/ConfirmationModal/ConfirmationModal";
import AssessmentIcon from '@mui/icons-material/Assessment';
import GroupIcon from '@mui/icons-material/Group';
import ReportCard from "../../../components/Reporting/Cards/ReportCard";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import GradeBookTable from "../../../components/Reporting/GradeBookTable/GradeBookTable";
import ReportModal from "../../../components/Reporting/Modals/ReportModal/ReportModal";
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

const navLinks = [
];

function FamilyDashboardPage() {
    const axiosInstance = useAxios();
    const theme = useTheme();
    const { status } = useContext(AuthContext);
    const navigate = useNavigate();
    const [quizzes, setQuizzes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isActivateAlertOpen, setIsActivateAlertOpen] = useState(false);
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const isMediumScreen = useMediaQuery(theme.breakpoints.between("sm", "md"));
    const [showAllNew, setShowAllNew] = useState(false);
    const [selectedQuiz, setSelectedQuiz] = useState(null);
    const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
    const [classData, setClassData] = useState(null);
    const [title, setTitle] = useState('Copy ID');
    const [studentsData, setStudentsData] = useState({});
    const [quizzesForTable, setQuizzesForTable] = useState([]);
    const [reportModalOpen, setReportModalOpen] = useState(false);
    const [currentReport, setCurrentReport] = useState(null);
    
    // Determine initial display count based on screen size 
    let initialDisplayCount = 3;
    if (isSmallScreen) {
        initialDisplayCount = 1;
    } else if (isMediumScreen) {
        initialDisplayCount = 2;
    }

    const fetchQuizzes = async () => {
        try {
            const response = await axiosInstance.get("/quizzes/owner");
            if (response.data.success) {
                // Sort quizzes by updated_at in descending order
                const sortedQuizzes = response.data.quizzes.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
                setQuizzes(sortedQuizzes);
            } else {
                // setError(response.data.message || "Failed to fetch quizzes.");
                toast.error(response.data.message || "Failed to fetch quizzes.");
            }
        } catch (err) {
            // setError(err.response?.data?.message || "An unexpected error occurred.");
            toast.error(err.response?.data?.message || "An unexpected error occurred.");
        } finally {
            setLoading(false);
        }
    };

    const fetchClassData = async () => {
        try {
            const response = await axiosInstance.get("/classes/family");
            if (response.data.success) {
                // Sort quizzes by updated_at in descending order
                setClassData(response.data.class);
            } else {
                // setError(response.data.message || "Failed to fetch class data.");
                toast.error(response.data.message || "Failed to fetch class data.");
            }
        } catch (err) {
            // setError(err.response?.data?.message || "An unexpected error occurred.");
            navigate('/setup-profile');
            toast.error(err.response?.data?.message || "An unexpected error occurred.");
        } finally {
            setLoading(false);
        }
    }

    const prepareGradeBookData = (classObject) => {
        if (!classObject || !classObject.quizzes) return;
        
        const quizzesArray = classObject.quizzes; // an array of { id, title, sessions[] }
      
        // Build the data structure
        const tempStudentsData = {};
        
        quizzesArray.forEach((quiz) => {
          quiz.sessions.forEach((session) => {
            const user = session.user;
            // only if user.role === 'student' or you want to show for teachers too
            const userId = user.id;
            const fullName = user.student_profile
              ? user.student_profile.full_name
              : user.teacher_profile
              ? user.teacher_profile.full_name
              : user.profile
              ? user.profile.full_name
              : user.username;
      
            if (!tempStudentsData[userId]) {
              tempStudentsData[userId] = {
                id: userId,
                name: fullName,
                quizzes: {}
              };
            }
      
            if (!tempStudentsData[userId].quizzes[quiz.id]) {
              tempStudentsData[userId].quizzes[quiz.id] = [];
            }
      
            tempStudentsData[userId].quizzes[quiz.id].push({
              attempt: session.attempt,
              final_evaluation: session.final_evaluation,
              session_id: session.id
            });
          });
        });
      
        // quizzesForTable can just be id + title
        const quizList = quizzesArray.map(q => ({
          id: q.id,
          title: q.title,
        }));
      
        setStudentsData(tempStudentsData);
        setQuizzesForTable(quizList);
      };

    useEffect(() => {
        fetchClassData();
        fetchQuizzes();
    }, []);

    useEffect(() => {
        if (classData) {
          prepareGradeBookData(classData);
        }
      }, [classData]);

    const handleEditQuiz = (quizId) => {
        if(status === 'trial') {
            setIsActivateAlertOpen(true);
        } else {
            navigate(`/quiz-review/${quizId}`);
        }
    };

    const handleDeleteQuiz = async (quizId) => {
        if (status === 'trial') {
            setIsActivateAlertOpen(true);
        } else {
            setSelectedQuiz(quizId);
            setIsConfirmationModalOpen(true); 
        }
    };

    const handleCloseConfirmationModal = () => {
        setIsConfirmationModalOpen(false);
    };

    const handleDelete = async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.delete(`/quizzes/${selectedQuiz}`);
            if (response.data.success) {
                // Update quizzes after deletion
                fetchQuizzes();
                handleCloseConfirmationModal();
            } else {
                setError(response.data.message || "Failed to delete quiz.");
            }
        } catch (err) {
            setError(err.response?.data?.message || "An unexpected error occurred.");
        } finally {
            setLoading(false);
        }
    };

    const handleCreateQuiz = () => {
        // navigate('/quiz/create');
        navigate('/quiz/create', { state: { classId: classData.id } })
    };

    const reloadPage = () => {
        window.location.reload(); // Reload the page
    };

    // Determine which quizzes to show
    const displayedQuizzes = showAllNew ? quizzes : quizzes.slice(0, initialDisplayCount);

    const totalQuizzes = quizzes.length;

    const isSubsetNew = !showAllNew && totalQuizzes > displayedQuizzes.length;

    // Compute displayed name from user object
    const getUserDisplayName = (user) => {
        if (user.role === 'student' && user.student_profile) {
            return user.student_profile.full_name;
        } else if (user.family_profile) {
            return user.family_profile.family_name;
        }
        return user.username;
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(classData?.unique_identifier || "");
        setTitle('Copied!');
      };

      const handleViewReportClick = async (sessionId) => {
        try {
          const response = await axiosInstance.get(`/sessions/${sessionId}/report`);
          if (response.data.success) {
            setCurrentReport(response.data.report);
            setReportModalOpen(true);
          } else {
            // setError(response.data.message || "Failed to fetch report.");
            toast.error(response.data.message || "Failed to fetch report.");
          }
        } catch (err) {
        //   setError(err.response?.data?.message || "An unexpected error occurred.");
        toast.error(err.response?.data?.message || "Report not found or an unexpected error occurred.");
        }
      };

    let content;
    if (loading) {
        content = (
            <Box className="loading-container">
                <CircularProgress />
                <Typography variant="h6" className="loading-text">
                    Fetching Assessments...
                </Typography>
            </Box>
        );
    } else if (error) {
        content = (
            <Box className="error-container">
                <Typography variant="h6" color="error">
                    {error}
                </Typography>
            </Box>
        );
    } else {
        content = (
            <>
            {/* Class Info Section */}
            <Box className="class-info-card ">
                <Box className="class-info-header">
                    <Typography variant="h5" className="class-title">
                    {classData?.class_name}
                    </Typography>
                    <Box className="class-id-section">
                    <Typography variant="body2" className="class-id-title">
                        Class ID <br /> <span className="class-id-value">{classData?.unique_identifier}</span> 
                    </Typography>
                    <Tooltip title={classData?.title}>
                        <IconButton onClick={handleCopy}>
                        <ContentCopyIcon />
                        </IconButton>
                    </Tooltip>
                    </Box>
                </Box>
                <Typography variant="body1" className="class-description">
                    {classData?.description || "No description provided."}
                </Typography>
                <Typography className="family-support-message">
                    if you need any help, there is a dedicated support person available to help you
                </Typography>
                <Typography className="family-support-message">
                   <WhatsAppIcon /> +971 (0) 509910248
                </Typography>
            </Box>
                {/* New Quizzes Section */}
                <ContentHeader
                    title="Your Assessments"
                    buttonLabel="Create Assessment"
                    onButtonClick={handleCreateQuiz}
                />
                <div className="quizzes-grid my-5">
                    {displayedQuizzes.length !== 0 ? (
                        displayedQuizzes.map((quiz) => (
                            <QuizCard
                                key={quiz.id}
                                quiz={quiz}
                                onEditClick={() => handleEditQuiz(quiz.id)}
                                onDeleteClick={() => handleDeleteQuiz(quiz.id)}
                                onReload={reloadPage}
                            />
                        ))
                    ) : (
                        <Typography className="mb-5">No Assessments to show</Typography>
                    )}
                </div>
                {(isSubsetNew || (showAllNew && totalQuizzes > initialDisplayCount)) && (
                    <Box display="flex" justifyContent="center" alignItems="center" mt={2}>
                        <Typography variant="body2" sx={{ mr: 2 }} className="mb-5">
                            {showAllNew
                                ? `Showing all ${totalQuizzes} Assessments`
                                : `Showing ${displayedQuizzes.length} of ${totalQuizzes} Assessments`}
                        </Typography>
                        <Button
                            className="text-button mb-5"
                            onClick={() => setShowAllNew(!showAllNew)}
                        >
                            {showAllNew ? "View Less" : "View All"}
                        </Button>
                    </Box>
                )}
                {/* Reporting Section */}
                <ContentHeader
                    title="Reporting"
                />
                <div className="reports-grid mb-5">
                    {/* Active Sessions Report Card */}
                    <ReportCard
                        icon={AssessmentIcon}
                        title="Active Sessions"
                        description="Monitor all active sessions of students currently taking your Assessments. Stay updated with their real-time progress and performance."
                        buttonLabel="View Active Sessions"
                        onButtonClick={() => navigate('/reporting/active-sessions')}
                    />
                    {/* Attempted Users Report Card */}
                    <ReportCard
                        icon={GroupIcon}
                        title="Attempted Users"
                        description="View comprehensive reports of all students who have attempted your Assessments. Analyze their performance and insights."
                        buttonLabel="View Attempted Users"
                        onButtonClick={() => navigate('/reporting/attempted-users')}
                    />
                </div>
                {/* Grade Book */}
                <ContentHeader
                    title="Grade Book"
                />

                <GradeBookTable
                quizzes={quizzesForTable}
                studentsData={studentsData}
                onViewReport={handleViewReportClick}
                />

                <ReportModal
                open={reportModalOpen}
                onClose={() => setReportModalOpen(false)}
                report={currentReport}
                />
            </>
        );
    }

    return (
        <>
            <Header navLinks={navLinks} isScrollEffect={false} />
            <Container className="owner-quizzes-container page-container">
                {content}
                <ActivateAccountAlert
                    open={isActivateAlertOpen}
                    onClose={() => setIsActivateAlertOpen(false)}
                />
            </Container>
            <ConfirmationModal
                open={isConfirmationModalOpen}
                onClose={handleCloseConfirmationModal}
                isDanger={true}
                title="Are you sure you want to delete this Assessment?"
                onSubmit={handleDelete}
                hasNotes={false}
            />
            <ToastContainer position="top-right" autoClose={5000} />
        </>
    );
}

export default FamilyDashboardPage;
